import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import LeadForm from '@/components/LeadForm';
import { API_URL } from '@/lib/utils';
import { 
  Shield, Award, FlaskConical, GraduationCap, Laptop, Building, 
  FileCheck, Presentation, CheckCircle, ArrowRight, ArrowLeft, Phone
} from 'lucide-react';

const iconMap = {
  Shield, Award, FlaskConical, GraduationCap, Laptop, Building, FileCheck, Presentation
};

const ServiceDetail = () => {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [relatedServices, setRelatedServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchService = async () => {
      try {
        const [serviceRes, allServicesRes] = await Promise.all([
          axios.get(`${API_URL}/services/${slug}`),
          axios.get(`${API_URL}/services?active_only=true`)
        ]);
        setService(serviceRes.data);
        // Get related services from same category
        const related = allServicesRes.data
          .filter(s => s.category === serviceRes.data.category && s.slug !== slug)
          .slice(0, 3);
        setRelatedServices(related);
      } catch (error) {
        console.error('Error fetching service:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchService();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Service Not Found</h1>
        <Button asChild>
          <Link to="/services">Back to Services</Link>
        </Button>
      </div>
    );
  }

  const IconComponent = iconMap[service.icon] || Shield;

  return (
    <div data-testid="service-detail-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <Link to="/services" className="inline-flex items-center text-[#1a2a5e] hover:text-[#2eb24c] mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Services
          </Link>
          
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 bg-[#1a2a5e] rounded-2xl flex items-center justify-center">
                  <IconComponent className="h-8 w-8 text-white" />
                </div>
                <span className="text-[#2eb24c] font-semibold bg-[#2eb24c]/10 px-4 py-1 rounded-full">
                  {service.category}
                </span>
              </div>
              
              <h1 className="font-manrope text-3xl md:text-4xl font-bold text-[#1a2a5e] mb-6">
                {service.title}
              </h1>
              
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                {service.full_description}
              </p>
              
              {service.features && service.features.length > 0 && (
                <div className="mb-8">
                  <h3 className="font-manrope font-bold text-lg text-[#1a2a5e] mb-4">What's Included</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {service.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <CheckCircle className="h-5 w-5 text-[#2eb24c] flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex flex-wrap gap-4">
                <Button asChild size="lg" className="bg-[#1a2a5e] hover:bg-[#2d4a8e]">
                  <Link to="/contact">
                    Get Started <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="border-[#1a2a5e] text-[#1a2a5e]">
                  <a href="tel:+919616298162">
                    <Phone className="mr-2 h-5 w-5" />
                    Call Us Now
                  </a>
                </Button>
              </div>
            </div>
            
            {/* Lead Form */}
            <Card className="shadow-2xl border-0">
              <div className="bg-[#1a2a5e] text-white p-6">
                <h3 className="font-manrope text-xl font-bold">Request a Quote</h3>
                <p className="text-blue-100 text-sm mt-1">Our experts will contact you within 24 hours</p>
              </div>
              <CardContent className="p-6">
                <LeadForm source={`service-${service.slug}`} />
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Related Services */}
      {relatedServices.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="font-manrope text-2xl font-bold text-[#1a2a5e] mb-8">Related Services</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedServices.map((relatedService) => {
                const RelatedIcon = iconMap[relatedService.icon] || Shield;
                return (
                  <Link key={relatedService.id} to={`/services/${relatedService.slug}`}>
                    <Card className="h-full hover:shadow-lg transition-shadow">
                      <CardContent className="p-6">
                        <div className="w-12 h-12 bg-[#1a2a5e]/10 rounded-xl flex items-center justify-center mb-4">
                          <RelatedIcon className="h-6 w-6 text-[#1a2a5e]" />
                        </div>
                        <h3 className="font-manrope font-bold text-lg text-[#1a2a5e] mb-2">
                          {relatedService.title}
                        </h3>
                        <p className="text-gray-600 text-sm">{relatedService.short_description}</p>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-[#1a2a5e] text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-manrope text-3xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-blue-100 mb-8">
            Let our experts help you achieve your healthcare quality goals.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" className="bg-[#2eb24c] hover:bg-[#259a40]">
              <Link to="/contact">Schedule Consultation</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-[#1a2a5e]">
              <a href="tel:+919616298162">
                <Phone className="mr-2 h-5 w-5" />
                +91 9616298162
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
